import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button, LinkButton } from './Button';
import { Panel } from './Panel';
import { PublicFrame } from './Shell';
import { TechnicalDetails } from './ProblemNotice';
import { SupportLink } from './SupportLink';
import { useTenant } from '../lib/tenant-context';

const chunkFailed = (e: unknown): boolean =>
  e instanceof Error &&
  /dynamically imported module|Loading chunk|Importing a module script failed/i.test(e.message);

/**
 * What a person sees when a screen throws or its code cannot be fetched. Replaces React Router's
 * developer page, which is written for developers and not in this product's voice. The message is
 * plain; the detail is there for whoever is asked to look into it.
 */
export function RouteError() {
  const tenant = useTenant();
  const error = useRouteError();
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  const title = isRouteErrorResponse(error)
    ? error.status === 404
      ? 'There is no screen at this address.'
      : 'This screen could not be loaded.'
    : chunkFailed(error) && offline
      ? 'This screen is not available offline.'
      : 'This screen could not be shown.';
  const body = offline
    ? 'Connect to the internet and try again.'
    : 'Reload the page. If it keeps failing, contact support.';

  return (
    <PublicFrame>
      <Panel padding="lg" role="alert">
        <h1 className="t-title rail:text-[27px] text-ink">{title}</h1>
        <p className="mt-2 text-inkMuted">{body}</p>
        <TechnicalDetails>
          <p>
            {isRouteErrorResponse(error)
              ? `Response status: ${error.status}`
              : chunkFailed(error)
                ? 'Page module download failed.'
                : 'Page rendering failed.'}
          </p>
          <p>Build: {tenant.git_sha}</p>
        </TechnicalDetails>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => window.location.reload()}>Try again</Button>
          <LinkButton to="/" variant="secondary">
            Go home
          </LinkButton>
          <SupportLink tenant={tenant} topic="Page could not load" />
        </div>
      </Panel>
    </PublicFrame>
  );
}
